/**
 * Boardroom V2 Model Adapter (Gate 3 / Task 3.1).
 *
 * Normalizes ExecutionOverviewResponse from /api/v1/execution/overview into presentation view models.
 *
 * Invariants:
 * 1. Presentation-only mapping: Never derives or alters RiskState, RiskSeverity, or readiness.
 * 2. Metric Contract Discipline: A percentage is rendered ONLY if backed by an authoritative numeric contract.
 *    Readiness and Execution Risk are strictly categorical with value=null.
 * 3. Degraded-Safe: Unavailable previews (Canvas, Chat, Worktree, Agents) are normalized with explicit unavailable status.
 */

export function mapTone(label) {
  if (!label) return 'neutral';
  const l = String(label).toLowerCase();
  if (['on track', 'low', 'sufficient', 'approved', 'active', 'healthy', 'good'].includes(l)) {
    return 'good';
  }
  if (['moderate', 'partial', 'pending', 'warn', 'warning'].includes(l)) {
    return 'warn';
  }
  if (['at risk', 'high', 'critical', 'overdue', 'weak', 'missing', 'bad', 'danger'].includes(l)) {
    return 'bad';
  }
  return 'neutral';
}

export function buildBoardroomV2Model(raw) {
  const data = raw?.overview || raw || {};

  const tenantId = data.tenant_id || 'default';
  const organizationId = data.organization_id || null;
  const programId = data.program_id || 'default_program';
  const programName = data.program_name || 'Executive Overview';
  const timeWindowDays = Number(data.time_window_days) || 14;
  const reportSnapshotId = data.report_snapshot_id || null;
  const generatedAt = data.generated_at || null;

  // 1. Posture: Decision Readiness (Categorical only)
  const readinessRaw = data.readiness || {};
  const readinessState = readinessRaw.state_label || 'Unknown';
  const readiness = {
    metricId: 'readiness',
    stateLabel: readinessState,
    value: null, // No fake percentage
    formattedValue: '--',
    tone: mapTone(readinessState),
    isNumeric: false,
    authority: readinessRaw.authority || 'authoritative_readiness',
  };

  // 2. Posture: Execution Risk (Categorical only)
  const riskRaw = data.execution_risk || {};
  const riskState = riskRaw.state_label || 'Unknown';
  const executionRisk = {
    metricId: 'execution_risk',
    stateLabel: riskState,
    value: null, // No fake percentage
    formattedValue: '--',
    tone: mapTone(riskState),
    isNumeric: false,
    authority: riskRaw.authority || 'authoritative_risk_engine',
  };

  // 3. Posture: Evidence Posture (Authoritative numeric percentage on 0..100 scale)
  const evidenceRaw = data.evidence_posture || {};
  const evidenceState = evidenceRaw.state_label || 'Missing';
  const hasEvidenceValue = typeof evidenceRaw.value === 'number';
  const evidencePosture = {
    metricId: 'evidence_posture',
    stateLabel: evidenceState,
    value: hasEvidenceValue ? evidenceRaw.value : null,
    formattedValue: hasEvidenceValue ? `${Math.round(evidenceRaw.value)}%` : '--',
    tone: mapTone(evidenceState),
    isNumeric: hasEvidenceValue,
    authority: evidenceRaw.authority || 'authoritative_evidence',
    unit: evidenceRaw.unit || 'percentage',
    scale: evidenceRaw.scale || '0..100',
  };

  // 4. Posture: Top Execution Risks
  const rawRisks = Array.isArray(data.top_risks) ? data.top_risks : [];
  const topRisks = rawRisks.map((r) => ({
    id: r.risk_id || 'r_unknown',
    title: r.title || 'Untitled risk',
    severity: (r.severity || 'MODERATE').toUpperCase(),
    trend: r.trend || 'stable',
    confidencePct: typeof r.confidence_pct === 'number' ? r.confidence_pct : null,
    state: (r.state || 'ACTIVE').toUpperCase(),
    knowledgeSufficiency: r.knowledge_sufficiency || 'unknown',
    category: r.category || null,
    canonicalRef: r.canonical_ref || null,
    tone: mapTone(r.severity),
  }));

  // 5. Posture: Key Decisions
  const decisionsRaw = data.key_decisions || {};
  const keyDecisions = {
    pending: Number(decisionsRaw.pending) || 0,
    approved: Number(decisionsRaw.approved) || 0,
    overdue: Number(decisionsRaw.overdue) || 0,
    total: Number(decisionsRaw.total) || 0,
  };

  // 6. Posture: Active AI Agents
  const agentsRaw = data.active_agents || {};
  const activeAgents = {
    available: Boolean(agentsRaw.available),
    status: agentsRaw.status || 'unavailable',
    running: Number(agentsRaw.running) || 0,
    waiting: Number(agentsRaw.waiting) || 0,
    idle: Number(agentsRaw.idle) || 0,
    reason: agentsRaw.reason || 'agent_runtime_uninitialized',
    label: agentsRaw.available ? `${agentsRaw.running || 0} Running` : 'Runtime Inactive',
    tone: agentsRaw.available ? 'good' : 'neutral',
  };

  // 7. Previews: Canvas
  const canvasRaw = data.canvas_preview || {};
  const canvasPreview = {
    available: Boolean(canvasRaw.available),
    status: canvasRaw.status || 'unavailable',
    activePeople: Number(canvasRaw.active_people) || 0,
    activeAgents: Number(canvasRaw.active_agents) || 0,
    validatedArtifacts: Number(canvasRaw.validated_artifacts) || 0,
    latestActivityExcerpt: canvasRaw.latest_activity_excerpt || null,
    deepLink: canvasRaw.deep_link || 'talkto://workspace/default/canvas',
    reason: canvasRaw.reason || 'collaboration_engine_uninitialized',
  };

  // 8. Previews: Chat / Organizational Intelligence
  const chatRaw = data.chat_preview || {};
  const chatPreview = {
    available: Boolean(chatRaw.available),
    status: chatRaw.status || 'unavailable',
    recentThreadId: chatRaw.recent_thread_id || null,
    recentThreadTitle: chatRaw.recent_thread_title || null,
    citedResponseExcerpt: chatRaw.cited_response_excerpt || null,
    groundedSourcesCount: Number(chatRaw.grounded_sources_count) || 0,
    suggestedQueries: Array.isArray(chatRaw.suggested_queries) ? chatRaw.suggested_queries : [],
    deepLink: chatRaw.deep_link || 'talkto://chat/recent',
    reason: chatRaw.reason || 'retrieval_engine_uninitialized',
  };

  // 9. Previews: Worktree / Execution Graph
  const worktreeRaw = data.worktree_preview || {};
  const worktreePreview = {
    available: Boolean(worktreeRaw.available),
    status: worktreeRaw.status || 'unavailable',
    rootNodeId: worktreeRaw.root_node_id || null,
    rootLabel: worktreeRaw.root_label || null,
    rootStatus: worktreeRaw.root_status || null,
    previewNodesCount: Number(worktreeRaw.preview_nodes_count) || 0,
    criticalPathCount: Number(worktreeRaw.critical_path_count) || 0,
    deepLink: worktreeRaw.deep_link || 'talkto://worktree/root',
    reason: worktreeRaw.reason || 'execution_graph_uninitialized',
  };

  // 10. Insights Alerts
  const rawAlerts = Array.isArray(data.insights_alerts) ? data.insights_alerts : [];
  const alerts = rawAlerts.map((a) => ({
    alertId: a.alert_id || 'alt_unknown',
    category: a.category || 'risk',
    title: a.title || 'Overview Alert',
    detail: a.detail || '',
    actionLabel: a.action_label || 'View',
    actionTarget: a.action_target || '/boardroom',
    tone: mapTone(a.category === 'risk' ? 'bad' : (a.category === 'decision_overdue' ? 'warn' : 'info')),
  }));

  return {
    tenantId,
    organizationId,
    programId,
    programName,
    timeWindowDays,
    reportSnapshotId,
    generatedAt,
    posture: {
      readiness,
      executionRisk,
      evidencePosture,
      topRisks,
      keyDecisions,
      activeAgents,
    },
    previews: {
      canvas: canvasPreview,
      chat: chatPreview,
      worktree: worktreePreview,
    },
    alerts,
  };
}
